import {
  Button,
  EditButton,
  Icons,
  Show,
  Space,
  useModalForm,
  Tabs,
  Dropdown,
  Menu,
  Divider,
} from '@pankod/refine-antd'
import {
  IResourceComponentsProps,
  useCan,
  useDelete,
  useGetIdentity,
  useNavigation,
  useResource,
  useShow,
  useUpdate,
} from '@pankod/refine-core'
import { Dayjs } from 'dayjs'
import Head from 'next/head'
import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { TCommonError, TCommonResponse } from 'src/interfaces/common'
import { PopDeleteConfirm } from '../components/PopDeleteConfirm'
import { PROGRAM_STATE, TLMSProgramDetail, TLMSProgramRequest } from './types'
import { getAvailableStatus } from './utils'
import { ProgramDetail } from './sections/ProgramDetail'
import { ProgramLocalTableList } from '../program-lokal/blocks/TableList'
import { TableParticipantList } from '@components/DataTableParticipants'
import { trackTabChange } from 'src/helpers/url'
import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import CanvasMigrationTable from './sections/CanvasMigrationTable'
import ConfigurationButton from './sections/blocks/ConfigurationButton'
import { useProgramStore } from './store'
import ExportTimespendButton from './program-setup/blocks/ExportLaporanButton/ExportTimespendButton'
import ExportKeterisianButton from './program-setup/blocks/ExportLaporanButton/ExportKeterisianButton'
import DataAlterationApprovalTab from './sections/data-alteration-approval/DataAlterationApprovalTab'
import DownloadLaporanListModal from './sections/blocks/DownloadLaporanListModal'
import ExportUmpanBalikButton from './program-setup/blocks/ExportLaporanButton/ExportUmpanBalikButton'
import ExportProgramAttachmentButton from './program-setup/blocks/ExportLaporanButton/ExportProgramAttachmentButton'
import { castAsTuple, isSindaraOrigin } from '@resources/lms-management/types'
import EditProgramModalForm from '@resources/lms-management/program/blocks/EditProgramModalForm'
import ProgramLocalStatusStats from '@resources/lms-management/program/sections/blocks/ProgramLocalStatusStats'
import { NestedBreadcrumb } from '@resources/lms-management/components/NestedBreadcrumb'

const ALLOWED_PRODUCTION_EXPORT_KETERISIAN_PROGRAM_ID: string[] = ['69']
const PROGRAM_RESOURCE = 'programs'

export const LMSProgramManagementShow: React.FC<IResourceComponentsProps> = ({
  options,
}) => {
  const router = useRouter()
  const { activeTab = null } = router.query || {}
  const setProgram = useProgramStore(({ setProgram }) => setProgram)
  const resource = useResource()
  const { mutateAsync: doDelete } = useDelete()
  const id = resource.id as string
  const [activeKey, setActiveKey] = React.useState(
    (activeTab as string) || 'programLocal',
  )
  const { list, listUrl } = useNavigation()
  const [showDownloadLaporan, setShowDownloadLaporan] = useState(false)
  const { queryResult } = useShow<TCommonResponse<TLMSProgramDetail>>({
    id,
    resource: PROGRAM_RESOURCE,
    dataProviderName: 'lms',
  })
  useEffect(() => {
    if (queryResult.isSuccess) {
      setProgram({
        isOwner: Boolean(queryResult.data.data.data.programOwner),
        entityId: queryResult.data.data.data.entityId,
        periode: [
          queryResult.data.data.data.startDate,
          queryResult.data.data.data.endDate,
        ],
      })
    }
  }, [
    queryResult.data?.data.data.entityId,
    queryResult.data?.data.data.programOwner,
    queryResult.data?.data.data.startDate,
    queryResult.data?.data.data.endDate,
    queryResult.isSuccess,
    setProgram,
  ])

  const {
    modalProps: editModalProps,
    formProps: editFormProps,
    show: editShow,
    submit: editSubmit,
  } = useModalForm<TLMSProgramDetail, TCommonError, TLMSProgramRequest>({
    action: 'edit',
    resource: PROGRAM_RESOURCE,
    dataProviderName: 'lms',
    autoResetForm: true,
    submit: ({ periode, periodePendaftaran, ...formValues }) => {
      const [startDate, endDate] = periode as unknown as [Dayjs, Dayjs]
      const mappedPeriodePendaftaran = periodePendaftaran
        ? {
            pendaftaranStartDate: castAsTuple<Dayjs>(periodePendaftaran)[0]
              .startOf('day')
              .toISOString(),
            pendaftaranEndDate: castAsTuple<Dayjs>(periodePendaftaran)[1]
              .endOf('day')
              .toISOString(),
          }
        : {}
      return {
        name: formValues.name,
        status: formValues.status,
        startDate: startDate.startOf('day').format('YYYY-MM-DD'),
        endDate: endDate.endOf('day').format('YYYY-MM-DD'),
        tagIds: formValues.tags,
        ...mappedPeriodePendaftaran,
      }
    },
    successNotification: {
      message: 'Program berhasil diubah',
      type: 'success',
      description: 'Sukses',
    },
    errorNotification: (error) =>
      showErrorNotification(error, 'Terdapat gangguan saat mengubah program'),
    redirect: false,
  })

  const data = queryResult.data?.data.data

  const { data: identityData, isLoading } = useGetIdentity()
  const lmsRole = identityData?.user?.LMSRole || ''
  const status = getAvailableStatus(data?.status)

  const { mutateAsync, isLoading: isUpdatingStatus } = useUpdate<
    {},
    TCommonError,
    TLMSProgramRequest
  >()

  const { data: canAccessDataAlterationApproval } = useCan({
    action: 'show',
    resource: 'data-alteration-approval',
  })

  const { data: canAccessPresensiReport } = useCan({
    action: 'show',
    resource: 'lms-presensi-report',
  })

  const { data: canAccessPenilaianKinerjaReport } = useCan({
    action: 'show',
    resource: 'penilaian-kinerja-report',
  })

  const { data: canEditProgram } = useCan({
    action: 'edit',
    resource: 'lms-program-management',
  })

  const { data: canDeleteProgram } = useCan({
    action: 'delete',
    resource: 'lms-program-management',
  })

  const { data: canConfigProgram } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: {
      field: 'configuration',
    },
  })

  const { data: canExportTimespend } = useCan({
    action: 'field',
    resource: 'program-timespend',
    params: { field: 'export' },
  })

  const { data: canExportLaporanKeterisian } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'export-laporan-keterisian' },
  })

  const { data: canExportLaporanUmpanBalik } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'export-laporan-umpan-balik' },
  })

  const { data: canExportPenerbitanSertifikat } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'export-laporan-penerbitan-sertifikat' },
  })

  const { data: canAccessProgram } = useCan({
    action: 'show',
    resource: 'lms-program-management',
  })

  const isOwner = useProgramStore(({ isOwner }) => isOwner)

  const handleUpdateStatus = () => {
    if (!data || !status.nextState) return
    mutateAsync({
      id,
      resource: PROGRAM_RESOURCE,
      values: {
        endDate: data.endDate,
        name: data.name,
        startDate: data.startDate,
        status: status.nextState,
        pendaftaranEndDate: data.pendaftaranEndDate,
        pendaftaranStartDate: data.pendaftaranStartDate,
        tagIds: data.tags.map((tag) => tag.id),
      },
      dataProviderName: 'lms',
      successNotification: {
        message: 'Program berhasil diperbarui',
        type: 'success',
        description: 'Sukses',
      },
      errorNotification: (err) =>
        showErrorNotification(
          err,
          'Terdapat gangguan saat memperbarui Program',
        ),
    })
  }

  const isAdminEntitas = !isLoading && lmsRole === 'admin-entitas'
  const showPesertaTab = !isAdminEntitas || isOwner
  const showKeterisianButton =
    canExportLaporanKeterisian?.can &&
    (process.env.APP_ENV !== 'production' ||
      ALLOWED_PRODUCTION_EXPORT_KETERISIAN_PROGRAM_ID.includes(id))
  const showTimespendButton = canExportTimespend?.can
  const showUmpanBalikButton = canExportLaporanUmpanBalik?.can
  const showPenerbitanSertifikatButton = canExportPenerbitanSertifikat?.can
  const isSindaraProgram = isSindaraOrigin(data?.origin)
  return (
    <>
      <Show
        title="Detail Program"
        isLoading={queryResult.isLoading}
        breadcrumb={
          <NestedBreadcrumb
            injectedItems={[
              ...(canAccessProgram?.can
                ? [
                    {
                      label: 'Daftar Program',
                      href: listUrl('lms-program-management'),
                    },
                  ]
                : []),
              {
                label: `Program - ${id}`,
              },
            ]}
          />
        }
        headerButtons={() => (
          <Space>
            {(canConfigProgram?.can || isOwner) && (
              <ConfigurationButton
                programId={id}
                modifyProfileActive={
                  queryResult?.data?.data?.data?.modifyProfileActive
                }
                autoApproveDiklatParticipantsActive={
                  queryResult?.data?.data?.data
                    ?.autoApproveDiklatParticipantsActive
                }
                isSelfEnrollment={
                  queryResult?.data?.data?.data?.isSelfEnrollment
                }
                autoApproveModifyProfileActive={
                  queryResult?.data?.data?.data?.autoApproveModifyProfileActive
                }
                initialSisIdAccount={queryResult.data?.data?.data.sisAccountId}
                programName={queryResult.data?.data.data.name}
                programStatus={queryResult.data?.data.data.status}
                ppConfigStage={queryResult.data?.data.data.ppConfigStage}
                penilaianKinerjaConfigStage={
                  queryResult.data?.data.data.penilaianKinerjaConfigStage
                }
                jurnalFasilitasiConfigStage={
                  queryResult.data?.data.data.jurnalFasilitasiConfigStage
                }
                umpanBalikConfigStage={
                  queryResult.data?.data.data.umpanBalikConfigStage
                }
                timeTrackerActive={
                  queryResult.data?.data.data.timeTrackerActive
                }
                useNewCertificate={
                  queryResult?.data?.data.data.useNewCertificate
                }
              />
            )}

            <EditButton
              onClick={() => {
                editShow(id)
              }}
              disabled={!canEditProgram?.can && !isOwner}
            >
              Ubah Program
            </EditButton>
            <PopDeleteConfirm
              title={`Apakah Anda yakin ingin menghapus Program "${queryResult.data?.data?.data.name}"?`}
              placeholder="Tulis ulang nama Program"
              errorMessage="Nama program tidak sama"
              disabled={
                queryResult.data?.data.data.status !==
                  PROGRAM_STATE.PREPARATION ||
                (!canDeleteProgram?.can && !isOwner)
              }
              onConfirm={() =>
                doDelete(
                  {
                    id,
                    resource: PROGRAM_RESOURCE,
                    dataProviderName: 'lms',
                    successNotification: {
                      message: 'Program berhasil dihapus',
                      type: 'success',
                      description: 'Sukses',
                    },
                    errorNotification: (error) =>
                      showErrorNotification(
                        error,
                        'Terdapat gangguan saat menghapus program',
                      ),
                  },
                  {
                    onSuccess: () => list('lms-program-management', 'replace'),
                  },
                )
              }
              okButtonProps={{
                danger: true,
                type: 'default',
              }}
              okText="Delete"
              validateValue={queryResult.data?.data?.data.name!}
            >
              <Button
                danger
                icon={<Icons.DeleteOutlined />}
                disabled={
                  queryResult.data?.data.data.status !==
                    PROGRAM_STATE.PREPARATION ||
                  (!canDeleteProgram?.can && !isOwner)
                }
              >
                Hapus Program
              </Button>
            </PopDeleteConfirm>
          </Space>
        )}
      >
        <Head>
          <title>LMS Admin | {options?.label} </title>
        </Head>

        <ProgramDetail
          onUpdateStatus={handleUpdateStatus}
          data={data}
          status={status}
          isUpdatingStatus={isUpdatingStatus}
        />

        <ProgramLocalStatusStats programData={data} />

        <Divider dashed />

        <Tabs
          defaultActiveKey="programLocal"
          type="card"
          activeKey={activeKey}
          destroyInactiveTabPane
          onChange={(activeKey) => {
            setActiveKey(activeKey)
            trackTabChange(router, activeKey)
          }}
          tabBarExtraContent={{
            right: (
              <Dropdown
                placement="bottomRight"
                overlay={
                  <Menu
                    items={[
                      {
                        icon: <Icons.OrderedListOutlined />,
                        label: 'Nilai Akhir',
                        key: 'Nilai Akhir',
                        onClick: () => {
                          router.push(
                            `/reports-and-final-score?programId=${id}&activeTab=nilaiAkhir`,
                          )
                        },
                      },
                      ...(canAccessPresensiReport?.can
                        ? [
                            {
                              icon: <Icons.UnorderedListOutlined />,
                              label: 'Laporan Presensi',
                              key: 'Laporan Presensi',
                              onClick: () => {
                                router.push(
                                  `/reports-and-final-score?programId=${id}&activeTab=presensiReport`,
                                )
                              },
                            },
                          ]
                        : []),
                      ...(canAccessPenilaianKinerjaReport?.can || isOwner
                        ? [
                            {
                              icon: <Icons.UnorderedListOutlined />,
                              label: 'Laporan Penilaian Kinerja',
                              key: 'Laporan Penilaian Kinerja',
                              onClick: () => {
                                router.push(
                                  `/reports-and-final-score?programId=${id}&activeTab=penilaianKinerjaReport`,
                                )
                              },
                            },
                          ]
                        : []),
                      {
                        type: 'divider' as const,
                      },
                      ...(showTimespendButton
                        ? [
                            {
                              icon: <Icons.ExportOutlined />,
                              label: (
                                <ExportTimespendButton
                                  isOwner={isOwner}
                                  programId={id}
                                  can={showTimespendButton}
                                  onDaftarUnduhanClick={() => {
                                    setShowDownloadLaporan(true)
                                  }}
                                />
                              ),
                              key: 'Ekspor Laporan Timespend',
                              onClick: () => {},
                            },
                          ]
                        : []),
                      ...(showKeterisianButton
                        ? [
                            {
                              icon: <Icons.ExportOutlined />,
                              label: <ExportKeterisianButton programId={id} />,
                              key: 'Ekspor Laporan Keterisian',
                              onClick: () => {},
                            },
                          ]
                        : []),
                      ...(showUmpanBalikButton
                        ? [
                            {
                              icon: <Icons.ExportOutlined />,
                              label: (
                                <ExportUmpanBalikButton
                                  programId={id}
                                  can={showUmpanBalikButton}
                                  onDaftarUnduhanClick={() => {
                                    setShowDownloadLaporan(true)
                                  }}
                                />
                              ),
                              key: 'Ekspor Laporan Umpan Balik',
                              onClick: () => {},
                            },
                          ]
                        : []),
                      ...(showPenerbitanSertifikatButton
                        ? [
                            {
                              icon: <Icons.ExportOutlined />,
                              label: (
                                <ExportProgramAttachmentButton
                                  programId={id}
                                  can={showPenerbitanSertifikatButton}
                                  onDaftarUnduhanClick={() => {
                                    setShowDownloadLaporan(true)
                                  }}
                                  exportTypeOption={
                                    'laporan_penerbitan_sertifikat'
                                  }
                                  description="Apakah Anda ingin mengunduh laporan data penerbitan sertifikat di program ini?"
                                >
                                  Ekspor Laporan Penerbitan Sertifikat
                                </ExportProgramAttachmentButton>
                              ),
                              key: 'Ekspor Laporan Penerbitan Sertifikat',
                              onClick: () => {},
                            },
                          ]
                        : []),
                      ...(showTimespendButton ||
                      showKeterisianButton ||
                      showUmpanBalikButton ||
                      showPenerbitanSertifikatButton
                        ? [
                            {
                              type: 'divider' as const,
                            },
                            {
                              icon: <Icons.DownloadOutlined />,
                              label: 'Daftar Unduhan Laporan',
                              key: 'Daftar Unduhan Laporan',
                              onClick: () => {
                                setShowDownloadLaporan(true)
                              },
                            },
                          ]
                        : []),
                    ]}
                  />
                }
              >
                <Button icon={<Icons.ReadFilled />}>Laporan dan Nilai</Button>
              </Dropdown>
            ),
          }}
        >
          <Tabs.TabPane tab="Program Lokal" key="programLocal">
            <ProgramLocalTableList
              programResourceName={PROGRAM_RESOURCE}
              programId={data?.id}
              programName={data?.name}
              programStatus={data?.status}
            />
          </Tabs.TabPane>
          {showPesertaTab && (
            <Tabs.TabPane tab="Peserta" key="peserta">
              <TableParticipantList
                pageType="PROGRAM_LIST"
                programStatus={queryResult.data?.data.data.status}
                disableAddParticipant={isSindaraProgram}
                disableParticipantMutation={isSindaraProgram}
                forceParticipantDetailsViewMode={isSindaraProgram}
              />
            </Tabs.TabPane>
          )}
          {canAccessDataAlterationApproval?.can && (
            <Tabs.TabPane
              tab={
                <>
                  <i>Request</i> Perubahan Data
                </>
              }
              key="dataAlterationApproval"
            >
              <DataAlterationApprovalTab programId={id} />
            </Tabs.TabPane>
          )}
          <Tabs.TabPane tab="Canvas Migration" key="canvasSync">
            <CanvasMigrationTable />
          </Tabs.TabPane>
        </Tabs>
      </Show>

      <EditProgramModalForm
        editFormProps={editFormProps}
        editModalProps={editModalProps}
        onSubmit={editSubmit}
      />

      <DownloadLaporanListModal
        visible={showDownloadLaporan}
        onClose={() => {
          setShowDownloadLaporan(false)
        }}
        programId={id}
      />
    </>
  )
}
